import { useState } from 'react';
import { FilterOption, Game } from '../types';
import { SEASON_STATS } from '../data/mockData';
import { useGames } from '../hooks/useGames';
import HeroBanner from '../components/HeroBanner';
import FilterPills from '../components/FilterPills';
import PicksScroll from '../components/PicksScroll';
import GameRow from '../components/GameRow';

function applyFilter(games: Game[], filter: FilterOption): Game[] {
  switch (filter) {
    case '≥ 25%':
      return games.filter((g: Game) => g.prob >= 25);
    case 'Dome':
      return games.filter((g: Game) =>
        g.tags.some((t) => t.label === 'Dome')
      );
    case 'Wind In':
      return games.filter((g: Game) =>
        g.tags.some((t) => t.label.includes('IN'))
      );
    case 'Cold':
      return games.filter((g: Game) =>
        g.tags.some((t) => t.label.includes('Cold'))
      );
    case 'Low O/U':
      return games.filter((g: Game) =>
        g.tags.some(
          (t) =>
            t.label.startsWith('O/U') &&
            parseFloat(t.label.split(' ')[1]) <= 7.5
        )
      );
    default:
      return games;
  }
}

export default function Today() {
  const [filter, setFilter] = useState<FilterOption>('All');
  const { games, loading, error, isLive } = useGames();

  const filtered = applyFilter(games, filter);
  const strongCount = games.filter(g => g.prob >= 20).length;

  return (
    <div>
      <HeroBanner stats={SEASON_STATS} strongCount={strongCount} isLive={isLive} />
      <FilterPills active={filter} setActive={setFilter} />

      {/* loading state */}
      {loading && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '1.4rem',
            color: '#8090B0',
            letterSpacing: 2,
          }}>
            Loading today's slate...
          </div>
          <div style={{
            fontSize: '0.72rem',
            color: '#CBD5E8',
            marginTop: 8,
          }}>
            Fetching games · pitchers · weather
          </div>
        </div>
      )}

      {/* error banner */}
      {error && !loading && (
        <div style={{
          margin: '12px 20px 0',
          padding: '10px 14px',
          background: '#FFF0F0',
          borderRadius: 8,
          fontSize: '0.72rem',
          color: '#DC2626',
          fontWeight: 500,
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* live data badge */}
      {isLive && !loading && (
        <div style={{
          margin: '12px 20px 0',
          padding: '8px 14px',
          background: '#DCFCE7',
          borderRadius: 8,
          fontSize: '0.72rem',
          color: '#15803D',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{
            width: 6, height: 6,
            background: '#15803D',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'livePulse 2s ease infinite',
          }} />
          Live data · MLB Stats API · Odds API · Open-Meteo
        </div>
      )}

      {!loading && (
        <>
          <PicksScroll games={games} />

          {/* section label */}
          <div style={{
            padding: '12px 20px 6px',
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            color: '#8090B0',
          }}>
            Full Slate · {filtered.length} Games
          </div>

          {/* game list */}
          {filtered.map((g: Game) => (
            <GameRow key={g.id} game={g} />
          ))}

          {filtered.length === 0 && (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              fontSize: '0.82rem',
              color: '#8090B0',
            }}>
              No games match this filter today.
            </div>
          )}
        </>
      )}
    </div>
  );
}
